import type { PaddleEnvironmentConfig } from '@modules/paddle-connect/types';

export interface PaddleHealthRow {
  key: 'receipt' | 'processing' | 'delivery';
  label: string;
  value: string;
  detail: string;
  tone?: 'error' | 'success';
}
export interface PaddleHealthView {
  rows: PaddleHealthRow[];
  notice: string | null;
  measuredAt: string | null;
}

const validCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
export function formatPaddleHealthTime(value: string | null | undefined): string | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' })
    .format(new Date(value)) + ' UTC';
}

/** Presentation only: absence is unknown; receipt, processing and delivery are independent. */
export function buildPaddleWebhookHealthView(config?: PaddleEnvironmentConfig,
  options: { refreshFailed?: boolean; loading?: boolean } = {}): PaddleHealthView {
  const health = config?.webhook;
  const projection = config?.webhookStatus;
  const measuredAt = formatPaddleHealthTime(projection?.measuredAt);
  const countersSince = formatPaddleHealthTime(health?.countersSince);
  const incomplete = projection?.status === 'partial' || projection?.coverage === 'partial';
  if (!health || !projection || !['current', 'stale'].includes(projection.status) ||
    projection.coverage !== 'complete' || !measuredAt || !countersSince || !validCount(health.receivedTotal)) {
    return { rows: [], measuredAt: null, notice: options.loading ? 'Checking webhook status…'
      : incomplete ? 'Measurements are incomplete. Verified totals are not available yet.'
        : 'Webhook measurements unavailable. Check again.' };
  }
  const historical = projection.status === 'stale' || options.refreshFailed || options.loading;
  const reason = options.loading ? 'Checking webhook status…' : options.refreshFailed
    ? 'Could not refresh status.' : 'Measurements are out of date.';
  const notice = historical ? `${reason} Showing the last verified measurements from ${measuredAt}.` : null;
  const lastReceived = formatPaddleHealthTime(health.lastReceivedAt);
  const lastFailure = formatPaddleHealthTime(health.lastFailureAt);
  const lastDelivered = formatPaddleHealthTime(health.lastDeliveredAt);
  const processingKnown = validCount(health.processingFailedTotal) && validCount(health.processingPendingTotal);
  const deliveryKnown = validCount(health.deliveryFailedTotal) && validCount(health.deliveryPendingTotal);
  const history = health.failureHistoryCoverage === 'complete' && validCount(health.failedTotal)
    ? health.failedTotal === 0 ? health.receivedTotal === 0 ? '' : 'No processing failures in this counting period.'
      : `${health.failedTotal} event${health.failedTotal === 1 ? '' : 's'} had a processing failure during this counting period. Historical count; it does not mean it is still failing.${lastFailure ? ` Last failure ${lastFailure}.` : ''}`
    : 'Processing failure history is incomplete.';
  const processingOutstanding = processingKnown &&
    (health.processingFailedTotal! > 0 || health.processingPendingTotal! > 0);
  const deliveryClear = deliveryKnown && health.deliveryFailedTotal === 0 && health.deliveryPendingTotal === 0;
  const delivered = deliveryClear && processingKnown && !processingOutstanding && !!lastDelivered;
  return { notice, measuredAt, rows: [
    { key: 'receipt', label: 'Verified receipts',
      value: health.receivedTotal === 0 ? 'No verified events received' : `${health.receivedTotal} received`,
      detail: `${lastReceived ? `Last received ${lastReceived}${health.lastEventType ? ` · ${health.lastEventType}` : ''}. `
        : health.receivedTotal > 0 ? 'Last receipt time unavailable. ' : ''}Counting since ${countersSince}.` },
    { key: 'processing', label: 'Processing',
      value: !processingKnown ? 'Processing measurements unavailable'
        : health.receivedTotal === 0 && !processingOutstanding ? 'No events to process'
          : `${health.processingFailedTotal} failed · ${health.processingPendingTotal} pending`,
      detail: history,
      tone: processingKnown && health.processingFailedTotal! > 0 ? 'error' : undefined },
    { key: 'delivery', label: 'Delivery',
      value: !deliveryKnown ? 'Delivery measurements unavailable'
        : !deliveryClear || delivered ? `${health.deliveryFailedTotal} failed · ${health.deliveryPendingTotal} pending`
          : processingOutstanding ? 'Awaiting processing'
            : health.receivedTotal === 0 ? 'No delivery yet' : 'No delivery confirmed',
      detail: lastDelivered ? `Last fully completed delivery ${lastDelivered}.`
        : deliveryKnown && health.deliveryFailedTotal > 0 ? 'Received successfully. Delivery still needs attention.'
          : deliveryKnown && health.deliveryPendingTotal > 0 ? 'Receipt confirmed. Delivery is still pending.' : '',
      tone: deliveryKnown && health.deliveryFailedTotal > 0 ? 'error' : delivered && !historical ? 'success' : undefined },
  ] };
}
